import qs from 'query-string';

import appSettings from 'wanderu-config';
import { carrierDataSelectors } from 'wanderu-ducks-carriers';
import { liveCheckActions } from 'wanderu-ducks-live-check';
import { partnerSelectors } from 'wanderu-ducks-partner-data';
import {
    i18nMoney,
    selectCurrency,
    selectLocale,
} from 'wanderu-i18n';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import { getAllConversionRates } from 'wanderu-service-currency';
import {
    OrdersService,
    TixLogger,
} from 'wanderu-service-wapi';

import frontEndValidations from '../../../frontEndValidations';
import {
    browserDetect,
} from '../../../utils';
import {
    actions as bookingActions,
    selectors as bookingSelectors,
} from '../../appstate';
import { createAssociatedAccountAction } from '../createAssociatedAccount';
import { fetchTripConfirmationAction } from '../fetchTripConfirmation';
import { pollBookingStatusAction } from '../pollBookingStatus';
import { pollInsuranceStatusAction } from '../pollInsuranceStatus';
import { validateFormAction } from '../validateForm';
import {
    actions,
    namespace,
    reducer,
    selectors,
} from './base';
import { selectors as ordersSelectors } from '../../ordersSlice';

const logger = Logger.getLogger('wanderu-app-booking:submitForm');

const FORM_INVALID = 'FORM_INVALID';
const BOOKING_IN_PROGRESS = 'BOOKING_IN_PROGRESS';
const PAYMENT_FAILURE = 'PAYMENT_FAILURE';
const PRICE_CHANGE = 'PRICE_CHANGE';

function processSubmission(tripId, processingId) {
    return async (dispatch) => {
        await dispatch(pollBookingStatusAction.poll(processingId));
        await dispatch(fetchTripConfirmationAction.fetch(tripId));
    };
}

function bookTrip(tripId, tripContext, baseBody) {
    return async (dispatch, getState) => {
        const {
            seats,
            wheelchairs,
            exchangeRateBase,
            selectedFaresMap,
        } = tripContext;

        const selectedFare = selectedFaresMap[tripId];
        const lcParams = {
            seats,
            selectedFare,
            tripId,
            wheelchairs,
        };
        const tripLiveCheck = bookingSelectors.selectTripLiveCheck(getState(), tripId);
        const {
            carrier,
            pricing,
        } = tripLiveCheck.tripData;
        const {
            preferredCurrency,
        } = carrierDataSelectors.selectCarrierData(getState(), carrier);
        const exchangeRate = exchangeRateBase[preferredCurrency];

        const body = {
            ...baseBody,
            metadata: {
                ...baseBody.metadata,
                exchange_rate: exchangeRate,
            },
            pricing,
            selected_fare: selectedFare,
            trip_id: tripId,
        };

        try {
            const orderId = ordersSelectors.selectOrderId(getState());

            if (!orderId) {
                const {
                    booking_session_id: bookingSessionId,
                    itinerary_id: itineraryId,
                } = baseBody;

                logger.warn('missingOrderId', { bookingSessionId, itineraryId });
            }

            const query = qs.stringify({ orderId });
            const url = query
                ? `wtix/v4/book?${query}`
                : 'wtix/v4/book';

            const response = await OrdersService.post(url, { body }, { suppressLogging: true });

            const {
                insuranceOrderId: oldInsuranceProcessingId,
                insuranceProcessingId: newInsuranceProcessingId,
                orderId: oldProcessingId,
                processingId: newProcessingId,
            } = response.result;

            let processingId = newProcessingId || oldProcessingId;
            let insuranceProcessingId = newInsuranceProcessingId || oldInsuranceProcessingId;

            dispatch(bookingActions.setProcessingId(tripId, processingId));

            const existingInsuranceProcessingId = bookingSelectors
                .selectInsuranceProcessingId(getState());

            if (!existingInsuranceProcessingId && !!insuranceProcessingId) {
                // Unlike the processingId, the insuranceProcessingId is for the entire cart and
                // will be the same for every trip in a cart. We only need to set this once.

                dispatch(bookingActions
                    .setInsuranceProcessingId(processingId, insuranceProcessingId));
            }

            await dispatch(processSubmission(tripId, processingId));

            TixLogger.sendNow('submitForm', {
                booking_session_id: baseBody.bookingSessionId,
                itinerary_id: baseBody.itineraryId,
                processingId: processingId,
                trip_id: tripId,
            });
        } catch (ex) {
            const error = ex?.responseBody?.error || {};
            const {
                details = {},
                errorCategory,
                errorCode,
                statusCode,
            } = error;
            const formValidations = details?.validationResultElements?.fields;

            TixLogger.sendNow('submitFormError', {
                booking_session_id: baseBody.bookingSessionId,
                errorCategory,
                errorCode,
                formValidations: errorCode === FORM_INVALID
                    && formValidations,
                itinerary_id: baseBody.itineraryId,
                message: error.message,
                statusCode,
            });

            if (errorCode === FORM_INVALID) {
                const modifiedFields = bookingSelectors.selectFormWithModifications(getState());
                dispatch(actions.reset());

                if (formValidations) {
                    dispatch(bookingActions.setValidations({ fields: formValidations }));
                }

                dispatch(bookingActions.setBookingForm(modifiedFields));

                throw ex;
            }

            if (errorCode === BOOKING_IN_PROGRESS) {
                const {
                    processingId,
                    insuranceProcessingId,
                } = details;

                if (!processingId) {
                    // Something unexpected happened. Break out of loop
                    throw ex;
                }

                dispatch(bookingActions.setProcessingId(tripId, processingId));

                if (insuranceProcessingId) {
                    dispatch(bookingActions
                        .setInsuranceProcessingId(processingId, insuranceProcessingId));
                }

                // track the status of the existing booking and keep going
                await dispatch(processSubmission(tripId, processingId));
            }

            if (errorCode === PRICE_CHANGE) {
                const {
                    pricing: newPricing,
                } = details;
                // Update the lc and abort the booking flow

                const updatedTrip = {
                    ...tripLiveCheck,
                    tripData: {
                        ...tripLiveCheck.tripData,
                        pricing: {
                            ...pricing,
                            ...newPricing,
                        },
                    },
                };

                dispatch(liveCheckActions.setTripLiveCheck(lcParams, updatedTrip));

                throw ex;
            }

            // Error is not recoverable. Abort the booking
            throw ex;
        }
    };
}

function submitForm(_result, component) {
    return async (dispatch, getState) => {
        await dispatch(actions.reset());
        await dispatch(bookingActions.setAllFieldsModified());
        await dispatch(bookingActions.resetBookingStatus());
        await dispatch(bookingActions.resetInsuranceStatus());
        await dispatch(bookingActions.resetTripConfirmations());

        dispatch(actions.startWork());

        const bookingForm = bookingSelectors.selectFormData(getState());

        const {
            validatedForm,
            isValid,
        } = frontEndValidations(bookingForm);

        /**
         * Handle client-side validation failures.
         * Stores error in Redux so Processing modal can display it.
         * The form spans multiple steps, so users need the modal to indicate which fields need fixing.
         */
        const handleInvalid = (error) => {
            dispatch(actions.endWork(undefined, error));
            component?.setElementStatus('error');
        };

        const formError = {
            errorCategory: 'CLIENT_ERROR',
            errorCode: FORM_INVALID,
        };

        if (!isValid) {
            dispatch(bookingActions.setValidations({ fields: validatedForm }));
            handleInvalid(formError);
            return;
        }

        await dispatch(validateFormAction.validate({ isFormSubmission: true }));

        if (!bookingSelectors.selectAllFieldsAreValid(getState())) {
            handleInvalid(formError);
            return;
        }

        const payment = bookingSelectors.selectPayment(getState());
        const details = bookingSelectors.selectDetails(getState());

        const { billingAddress = {}, paymentMethod: { holderName } = {} } = payment || {};

        const testForCCNum = (value) => /(?:\d[ -]*?){12,20}/.test(value);
        const hasCreditCardNumber = Object.values({ holderName, ...billingAddress })
            .some(testForCCNum); // check if the value is a credit card number

        if (hasCreditCardNumber) { // if the holder name or billing address contains an unencrypted credit card number, show an error and end submitForm action
            const paymentError = {
                errorCategory: 'CLIENT_ERROR',
                errorCode: PAYMENT_FAILURE,
            };
            handleInvalid(paymentError);
            return;
        }

        const locale = selectLocale(getState());
        const currency = selectCurrency(getState());
        const {
            tripIds,
            itineraryId,
            seats,
            wheelchairs,
            bookingSessionId,
            selectedFaresMap = {},
            tokenMap = {},
        } = bookingSelectors.selectBookingInfo(getState());
        const exchangeRates = getAllConversionRates();
        const exchangeRateBase = exchangeRates[currency];
        const form = bookingSelectors.selectSlimFormData(getState());

        const partnerDetails = partnerSelectors.selectPartnerDetails(getState());
        const affiliateId = partnerDetails.affiliateId;
        const trackingId = partnerDetails.trackingId;

        const tripContext = {
            exchangeRateBase,
            seats,
            selectedFaresMap,
            wheelchairs,
        };

        const {
            browser,
            browserVersion,
            mobile,
        } = browserDetect();

        const baseBody = {
            accessibility: {
                wheelchairs,
            },
            booking_session_id: bookingSessionId,
            cacheTokens: Object.values(tokenMap),
            form_submission: { fields: form.fields },
            itinerary_id: itineraryId,
            locale,
            metadata: {
                currency,
                form_version: form.meta.formVersion,
                locale,
                pos: {
                    affiliateId,
                    affiliateTrackingId: trackingId,
                    browser,
                    browserVersion,
                    browserWidth: window.innerWidth,
                    clientName: window.location.hostname,
                    currency,
                    lang: locale.split('-')[0],
                    locale,
                    mobile,
                    screenWidth: window?.screen?.width, // evergreen browsers support the window API screen object, older ones may not
                    userAgent: navigator.userAgent,
                },
            },
            passengers: seats,
            payment: {
                ...details,
                ...payment,
            },
        };

        let error;
        let meta;
        let failedTripId = null;
        let isInsurancePhase = false;
        try {
            for (let tripId of tripIds) {
                failedTripId = tripId;
                // The return trip fetch depends on the success of the depart trip, so we want to fetch these in order, synchronously
                // eslint-disable-next-line no-await-in-loop
                await dispatch(bookTrip(tripId, tripContext, baseBody));
            }
            // All trips succeeded
            failedTripId = null;

            let insurancePromise = Promise.resolve();
            let createAccountPromise = Promise.resolve();

            const insuranceProcessingId = bookingSelectors.selectInsuranceProcessingId(getState());
            const { value: password } = bookingSelectors.selectPasswordField(getState());

            if (insuranceProcessingId) {
                isInsurancePhase = true;
                insurancePromise = dispatch(pollInsuranceStatusAction.poll(insuranceProcessingId));
            }

            if (password) {
                createAccountPromise = dispatch(createAssociatedAccountAction.create(password));
            }

            await Promise.all([insurancePromise, createAccountPromise]);
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            error = ex?.responseBody?.error || {};

            const { errorCode } = error;

            // Set booking status to ERROR on the item that actually failed.
            // When /book fails, no processingId is set, so we create a synthetic one.
            if (failedTripId) {
                // A trip failed - set error on that specific trip
                const errorProcessingId = `error-${failedTripId}`;
                dispatch(bookingActions.setProcessingId(failedTripId, errorProcessingId));
                dispatch(bookingActions.setBookingStatus(errorProcessingId, {
                    isProcessing: false,
                    isQueued: false,
                    isRecoverableError: true,
                    isRetryableError: false,
                    isRetrySearchError: false,
                    isUnknownError: false,
                    message: error.message || '',
                    status: 'ERROR',
                    statusCode: error.statusCode || 500,
                }));
            } else if (isInsurancePhase) {
                // Insurance failed - set error on insurance
                dispatch(bookingActions.setInsuranceStatus({
                    isProcessing: false,
                    isQueued: false,
                    isRecoverableError: true,
                    isRetryableError: false,
                    isRetrySearchError: false,
                    isUnknownError: false,
                    message: error.message || '',
                    status: 'ERROR',
                    statusCode: error.statusCode || 500,
                }));
            }

            // Transform PRICE_CHANGE details to match component expectations.
            // API returns pricing in carrier's currency (e.g., USD), which may
            // differ from user's display currency (e.g., GBP).
            // i18nMoney handles conversion + locale formatting automatically.
            if (errorCode === PRICE_CHANGE && error.details) {
                const { originalPricing, pricing } = error.details;
                if (originalPricing && pricing) {
                    const pricingCurrency = Object.keys(pricing)[0];
                    const oldPrice = originalPricing[pricingCurrency]?.price;
                    const newPrice = pricing[pricingCurrency]?.price;
                    if (oldPrice !== undefined && newPrice !== undefined) {
                        error.details.oldPrice = i18nMoney(oldPrice, pricingCurrency).format();
                        error.details.newPrice = i18nMoney(newPrice, pricingCurrency).format();
                    }
                }
            }

            if (errorCode === FORM_INVALID) {
                logger.warn('submitForm', ex, {
                    snowplowIds: {
                        networkUserId: SnowplowService.getNetworkUserId(),
                        ...domainUserInfo,
                    },
                });
            } else {
                logger.error('submitForm', ex, {
                    snowplowIds: {
                        networkUserId: SnowplowService.getNetworkUserId(),
                        ...domainUserInfo,
                    },
                });
            }

            // Error is stored in Redux via endWork() in finally block.
            // Processing modal will display the error.
            component?.setElementStatus('error');
        } finally {
            dispatch(actions.endWork(meta, error));
        }
    };
}

const submitFormAction = {
    submit: submitForm,
    ...selectors,
    ...actions,
};

export {
    submitFormAction,
};

const submitFormSlice = {
    [namespace]: reducer,
};

export default submitFormSlice;
